import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { NavIcon, type IconName } from '../components/NavIcon.tsx';
import type { Company, CompanyWithCounts, Driver, Asset } from '../lib/types.ts';
import { statusBuckets, assetBuckets, pct, isInProgress, type StatusBuckets } from './dashboardStats.ts';

// Chrome accent mirrors AdminShell (ACCENT #2B93FF); the compliant/not-compliant
// hues mirror the status-badge palette (StatusBadge.tsx) so the health panels
// read the same as badges elsewhere in the admin.
const ACCENT = '#2B93FF';
const GREEN = '#10B981';
const RED = '#EF4444';

type Segment = { label: string; value: number; color: string };

// Segments for a CompanyStatus-based panel: compliant → in progress → not
// compliant (best-to-worst), matching the big "compliant / total" headline.
function statusSegments(b: StatusBuckets): Segment[] {
  return [
    { label: 'Compliant', value: b.compliant, color: GREEN },
    { label: 'In progress', value: b.inProgress, color: ACCENT },
    { label: 'Not compliant', value: b.notCompliant, color: RED },
  ];
}

function ownerLabel(companyName: string | null | undefined, hasCompany: boolean): string {
  if (!hasCompany) return 'Operating company';
  return companyName ?? '—';
}

function assetLabel(a: Asset): string {
  const parts = [a.year ? String(a.year) : '', a.make ?? '', a.model ?? ''].filter(Boolean);
  return parts.join(' ') || a.registration_plate || 'Asset';
}

export function AdminDashboard() {
  const [queueDismissed, setQueueDismissed] = useState(false);

  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => adminApi.get<{ companies: Company[] }>('/api/companies'),
  });
  const driversQuery = useQuery({
    queryKey: ['drivers', 'all'],
    queryFn: () => adminApi.get<{ drivers: Driver[] }>('/api/drivers'),
  });
  const assetsQuery = useQuery({
    queryKey: ['assets'],
    queryFn: () => adminApi.get<{ assets: Asset[] }>('/api/assets'),
  });
  const queueQuery = useQuery({
    queryKey: ['review-queue'],
    queryFn: () => adminApi.get<{ companies: CompanyWithCounts[] }>('/api/companies/review/queue'),
  });

  if (companiesQuery.isLoading || driversQuery.isLoading || assetsQuery.isLoading) {
    return <CenteredSpinner />;
  }
  const primaryError = companiesQuery.error ?? driversQuery.error ?? assetsQuery.error;
  if (primaryError) return <ErrorMessage error={primaryError} />;

  const companies = companiesQuery.data?.companies ?? [];
  const drivers = driversQuery.data?.drivers ?? [];
  const assets = assetsQuery.data?.assets ?? [];
  const queue = queueQuery.data?.companies ?? [];

  const companyStats = statusBuckets(companies.map((c) => c.status));
  const driverStats = statusBuckets(drivers.map((d) => d.status));
  const assetStats = assetBuckets(assets);

  // Notification subtitle: split the review backlog into pending vs. changes-
  // requested so the banner mirrors the prototype's middot-separated detail line.
  const pending = queue.reduce((n, c) => n + (c.submission_counts?.pending ?? 0), 0);
  const rejected = queue.reduce((n, c) => n + (c.submission_counts?.rejected ?? 0), 0);
  const subtitleParts: string[] = [];
  if (pending > 0) subtitleParts.push(`${pending} pending`);
  if (rejected > 0) subtitleParts.push(`${rejected} changes requested`);
  const queueSubtitle =
    subtitleParts.length > 0 ? subtitleParts.join(' · ') : 'Submissions awaiting your review';

  // Work queues — entities that aren't compliant yet. Drivers share
  // CompanyStatus, so their queue drills into two buckets: `expired` ("Not
  // Compliant") and the in-progress states, non-compliant first since it's the
  // more urgent bucket. Assets carry only a binary compliance_status (active
  // only), so their queue stays non-compliant-only, matching the asset tile.
  const CAP = 6;
  const ncDrivers = drivers.filter((d) => d.status === 'expired');
  const inProgressDrivers = drivers.filter((d) => isInProgress(d.status));
  const driverQueue = [...ncDrivers, ...inProgressDrivers];
  const ncAssets = assets.filter(
    (a) => a.status === 'active' && a.compliance_status === 'not_compliant',
  );

  // Breakdown line under the driver queue title, mirroring the review banner's
  // middot-separated detail (e.g. "3 not compliant · 5 in progress").
  const driverParts: string[] = [];
  if (ncDrivers.length > 0) driverParts.push(`${ncDrivers.length} not compliant`);
  if (inProgressDrivers.length > 0) driverParts.push(`${inProgressDrivers.length} in progress`);
  const driverQueueSubtitle = driverParts.length > 0 ? driverParts.join(' · ') : undefined;

  // Recent activity: active companies only, newest first (deactivating bumps
  // updated_at, which would otherwise pin a parked company to the top).
  const activeCompanies = companies.filter((c) => c.status !== 'deactivated');
  const recent = [...activeCompanies]
    .sort((a, b) => (b.updated_at < a.updated_at ? -1 : 1))
    .slice(0, 6);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          Compliance across companies, drivers &amp; assets
        </p>
      </div>

      {queue.length > 0 && !queueDismissed && (
        <div
          className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
          style={{ borderLeftWidth: 4, borderLeftColor: '#F59E0B' }}
        >
          <NavIcon name="bell" size={20} className="shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-900">
              {queue.length} company{queue.length === 1 ? '' : 's'} awaiting review
            </div>
            <div className="text-xs text-amber-700">{queueSubtitle}</div>
          </div>
          <LinkBtn
            variant="primary"
            size="sm"
            to="/admin/companies?status=review"
            className="shrink-0"
            style={{ backgroundColor: ACCENT }}
          >
            Open queue →
          </LinkBtn>
          <button
            type="button"
            onClick={() => setQueueDismissed(true)}
            aria-label="Dismiss notification"
            className="shrink-0 rounded-md p-1.5 text-amber-500 transition hover:bg-amber-100 hover:text-amber-700"
          >
            <NavIcon name="x" size={16} />
          </button>
        </div>
      )}

      {/* Health panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <HealthPanel
          icon="building"
          title="Company compliance"
          to="/admin/companies"
          compliant={companyStats.compliant}
          total={companyStats.total}
          segments={statusSegments(companyStats)}
        />
        <HealthPanel
          icon="users"
          title="Driver compliance"
          to="/admin/drivers"
          compliant={driverStats.compliant}
          total={driverStats.total}
          segments={statusSegments(driverStats)}
        />
        <HealthPanel
          icon="truck"
          title="Asset compliance"
          to="/admin/assets"
          compliant={assetStats.compliant}
          total={assetStats.total}
          segments={[
            { label: 'Compliant', value: assetStats.compliant, color: GREEN },
            { label: 'Not compliant', value: assetStats.notCompliant, color: RED },
          ]}
        />
      </div>

      {/* Work queues — drivers/assets that aren't compliant yet */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WorkQueueCard
          icon="alert-triangle"
          iconClassName="text-amber-500"
          title="Drivers to action"
          subtitle={driverQueueSubtitle}
          count={driverQueue.length}
          countTone="neutral"
          emptyLabel="All drivers compliant"
          viewAllTo="/admin/drivers"
          rows={driverQueue.slice(0, CAP).map((d) => ({
            id: d.id,
            name: d.name,
            subtitle: ownerLabel(d.company?.company_name, d.company_id !== null),
            to: `/admin/drivers/${d.id}`,
            badge: <CompanyStatusBadge status={d.status} />,
          }))}
        />
        <WorkQueueCard
          icon="x-circle"
          iconClassName="text-red-500"
          title="Non-compliant assets"
          count={ncAssets.length}
          countTone="danger"
          emptyLabel="All assets compliant"
          viewAllTo="/admin/assets"
          rows={ncAssets.slice(0, CAP).map((a) => ({
            id: a.id,
            name: assetLabel(a),
            subtitle: `${a.asset_type ?? 'Asset'} · ${ownerLabel(
              a.company?.company_name,
              a.company_id !== null,
            )}`,
            to: `/admin/assets/${a.id}`,
            badge: <NotCompliantPill />,
          }))}
        />
      </div>

      {/* Recent activity */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Recent activity
          </h2>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Company</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Progress</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/companies/${c.id}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {c.company_name}
                    </Link>
                    <div className="text-xs text-slate-500">{c.contact_name}</div>
                  </td>
                  <td className="px-4 py-2">
                    <CompanyStatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-2 text-slate-700">{c.progress}%</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {new Date(c.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-500">
                    No companies yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HealthPanel({
  icon,
  title,
  to,
  compliant,
  total,
  segments,
}: {
  icon: IconName;
  title: string;
  to: string;
  compliant: number;
  total: number;
  segments: Segment[];
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: '#E8F4FF', color: ACCENT }}
          >
            <NavIcon name={icon} size={18} />
          </span>
          <span className="text-sm font-semibold text-slate-900">{title}</span>
        </div>
        <NavIcon
          name="chevron-right"
          size={16}
          className="text-slate-300 transition group-hover:text-slate-400"
        />
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-slate-900">
          {compliant}
          <span className="text-slate-300">/{total}</span>
        </span>
        <span className="text-sm text-slate-500">compliant</span>
      </div>

      {/* Segmented health bar */}
      <div className="mt-3 flex h-2 gap-0.5 overflow-hidden rounded-full bg-slate-100">
        {segments.map((s) => (
          <div key={s.label} style={{ width: `${pct(s.value, total)}%`, background: s.color }} />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
        {segments.map((s) => (
          <div key={s.label} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: s.color }}
                aria-hidden="true"
              />
              <span className="text-base font-semibold text-slate-900">{s.value}</span>
            </div>
            <span className="text-xs text-slate-500">{s.label}</span>
          </div>
        ))}
      </div>
    </Link>
  );
}

// A flat red "Not compliant" pill for the binary asset queue (assets don't carry
// a CompanyStatus, so they can't use CompanyStatusBadge like the driver rows do).
function NotCompliantPill() {
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
      Not compliant
    </span>
  );
}

type QueueRow = { id: string; name: string; subtitle: string; to: string; badge: ReactNode };

// A work-queue card: capped list of entities that need attention, each row
// carrying its own status badge. `count` is the full match total (drives both
// the header badge and the "View all" link); `rows` is the capped slice.
function WorkQueueCard({
  icon,
  iconClassName,
  title,
  subtitle,
  count,
  countTone,
  emptyLabel,
  viewAllTo,
  rows,
}: {
  icon: IconName;
  iconClassName: string;
  title: string;
  subtitle?: string | undefined;
  count: number;
  countTone: 'danger' | 'neutral';
  emptyLabel: string;
  viewAllTo: string;
  rows: QueueRow[];
}) {
  const countCls =
    countTone === 'danger' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-700';
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex min-w-0 items-center gap-2">
          <NavIcon name={icon} size={18} className={`shrink-0 ${iconClassName}`} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
        </div>
        {count > 0 && (
          <span
            className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${countCls}`}
          >
            {count}
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-6 text-sm text-slate-500">
          <NavIcon name="check-circle" size={18} className="text-emerald-500" />
          {emptyLabel}
        </div>
      ) : (
        <>
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between border-t border-slate-100 px-5 py-3"
            >
              <div className="min-w-0">
                <Link to={r.to} className="font-medium text-slate-900 hover:underline">
                  {r.name}
                </Link>
                <div className="truncate text-xs text-slate-500">{r.subtitle}</div>
              </div>
              <div className="ml-3 shrink-0">{r.badge}</div>
            </div>
          ))}
          {count > rows.length && (
            <Link
              to={viewAllTo}
              className="block border-t border-slate-100 px-5 py-3 text-sm font-medium hover:bg-slate-50"
              style={{ color: ACCENT }}
            >
              View all {count} →
            </Link>
          )}
        </>
      )}
    </div>
  );
}
